/**
 * @fileoverview Page for doctors to view their prescription history
 */
import React, { useRef, useCallback, useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Chip,
    Tooltip,
    Grid,
    InputAdornment,
    IconButton,
    CircularProgress,
    Divider,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PrintIcon from "@mui/icons-material/Print";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
    DmsPage,
    DmsAltBox,
    DmsTable,
    DmsButton,
    DmsModal,
    DmsTextField,
    DmsAlertWarning,
    DmsAlertInfo,
    DmsSelect,
    DmsTabber,
    intl,
    fetchData,
    HTTP_STATUS,
    Notifications
} from "dmsbasereact";
import dayjs from "dayjs";
import { openPrescriptionPdfForPrint } from "../../utils/downloadPrescriptionPdf";
import { formatCpf, normalizeGender } from "../../utils/formatters";
import { getGenderEnum } from "../../constants/GenderEnum";
import { PRESCRIPTION_STATUS } from "../../constants/PrescriptionStatus";
import { getIndicationOptions, getIndicationText as getIndicationLabel, IndicationEnum } from "../../constants/Indication";
import { isExpired } from "../../utils/expiration";
import { PrescriptionReason } from "../../constants/PrescriptionReason";
import { formatNotDispensedJustification } from "../../constants/NotDispensedReasonEnum";
/**
 * Convert a number to Portuguese words.
 * Handles numbers from 0 to 99.
 */
const numberToPortugueseWords = (n) => {
    if (!Number.isInteger(n) || n < 0) return n.toString();

    // Feminine forms for "cápsula" (feminine noun)
    const units = ["", "uma", "duas", "três", "quatro", "cinco", "seis", "sete", "oito", "nove"];
    const teens = ["dez", "onze", "doze", "treze", "quatorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"];
    const tens = ["", "", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"];

    if (n === 0) return "zero";
    if (n < 10) return units[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) {
        const ten = tens[Math.floor(n / 10)];
        const unit = units[n % 10];
        if (unit) return `${ten} e ${unit}`;
        return ten;
    }
    return n.toString();
};

const OTHER_REASON_VALUE = "OTHER";

const getStatusChip = (status, expirationDate) => {
    const expired = isExpired(expirationDate);

    if (status === PRESCRIPTION_STATUS.CANCELED) {
        return <Chip label={intl.get("Canceled").d("Canceled")} color="error" size="small" />;
    }
    if (status === PRESCRIPTION_STATUS.DISPENSED) {
        return <Chip label={intl.get("Dispensed").d("Dispensed")} color="success" size="small" />;
    }
    if (status === PRESCRIPTION_STATUS.NOT_DISPENSED) {
        return <Chip label={intl.get("NotDispensed").d("Not Dispensed")} color="warning" size="small" />;
    }
    if (expired) {
        return <Chip label={intl.get("Expired").d("Expired")} color="warning" size="small" />;
    }
    return <Chip label={intl.get("Active").d("Active")} color="primary" size="small" />;
};

const getIndicationText = (indication) => {
    return getIndicationLabel(indication) || '-';
};

export const PrescriptionHistoryPage = () => {
    const table = useRef(null);
    const [selectedPrescription, setSelectedPrescription] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [cancelReasonPk, setCancelReasonPk] = useState('');
    const [cancelCustomReason, setCancelCustomReason] = useState('');
    const [canceling, setCanceling] = useState(false);

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [, setEditDosage] = useState('');
    const [editIndication, setEditIndication] = useState('');
    const [editIndicationOther, setEditIndicationOther] = useState('');
    const [editReasonPk, setEditReasonPk] = useState('');
    const [editCustomReason, setEditCustomReason] = useState('');
    const [editPassword, setEditPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [editing, setEditing] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});

    // Edit posology state
    const [editDosageQuantity, setEditDosageQuantity] = useState('');
    const [editDosageDuration, setEditDosageDuration] = useState('');
    const [editDosageNotes, setEditDosageNotes] = useState('');
    const [editDosagePauseEnabled, setEditDosagePauseEnabled] = useState(false);
    const [editDosagePauseDays, setEditDosagePauseDays] = useState('');
    const [editSelectedMedication, setEditSelectedMedication] = useState(null);

    // Posology state
    const [posologies, setPosologies] = useState([]);
    const [selectedPosology, setSelectedPosology] = useState('');

    // Prescription reasons state
    const [prescriptionReasons, setPrescriptionReasons] = useState([]);

    // Prescription history state (for change history tab)
    const [prescriptionHistory, setPrescriptionHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Fetch posologies on mount
    useEffect(() => {
        const fetchPosologies = async () => {
            try {
                const response = await fetchData('/posology/all_with_parts', 'GET', true);
                if (response.status === HTTP_STATUS.OK_200) {
                    const posologiesData = response.data?.data || response.data || [];
                    if (Array.isArray(posologiesData)) {
                        setPosologies(posologiesData);
                    }
                }
            } catch (error) {
                console.error('Error fetching posologies:', error);
            }
        };
        fetchPosologies();
    }, []);

    // Fetch prescription reasons function (can be called to refresh)
    const fetchPrescriptionReasons = useCallback(async () => {
        try {
            const response = await fetchData('/prescription_reason/all', 'GET', true);
            if (response.status === HTTP_STATUS.OK_200) {
                // Handle different response formats
                let reasonsData = [];
                if (response.data?.data) {
                    reasonsData = Array.isArray(response.data.data) ? response.data.data :
                                 (response.data.data?.rows || []);
                } else if (Array.isArray(response.data)) {
                    reasonsData = response.data;
                }
                setPrescriptionReasons(reasonsData);
            }
        } catch (error) {
            console.error('Error fetching prescription reasons:', error);
            setPrescriptionReasons([]);
        }
    }, []);

    // Fetch prescription reasons on mount
    useEffect(() => {
        fetchPrescriptionReasons();
    }, [fetchPrescriptionReasons]);

    // Fetch prescription history (versions)
    const fetchPrescriptionHistory = useCallback(async (prescriptionPk) => {
        setLoadingHistory(true);
        try {
            const response = await fetchData(`/prescription/${prescriptionPk}/history`, 'GET', true);
            if (response.status === HTTP_STATUS.OK_200) {
                setPrescriptionHistory(response.data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching prescription history:', error);
            setPrescriptionHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    }, []);

    // Build prescription reason options - filter out OTHER from DB and add manually at end
    const prescriptionReasonOptions = useMemo(() => {
        const dbReasons = prescriptionReasons
            .filter(r => r && r.pk && r.name && r.name.toUpperCase() !== "OTHER")
            .map(r => ({ value: r.pk, label: intl.get(r.name).d(r.name) }));

        // Add "Outros" manually at the end
        return [
            ...dbReasons,
            { value: OTHER_REASON_VALUE, label: intl.get("OTHER").d("Other") }
        ];
    }, [prescriptionReasons]);

    const editPrescriptionReasonOptions = useMemo(() => {
        const excluded = ["OTHER", "INCORRECT_PATIENT", "DEATH", "PREGNANCY"];
        return prescriptionReasons
            .filter(r => r && r.pk && r.name && !excluded.includes(r.name.toUpperCase()))
            .map(r => ({ value: r.pk, label: intl.get(r.name).d(r.name) }));
    }, [prescriptionReasons]);

    // Check if selected reason is "Other" for cancel
    const isCancelOtherSelected = useMemo(() => {
        return cancelReasonPk === OTHER_REASON_VALUE;
    }, [cancelReasonPk]);

    // Check if selected reason is "Other" for edit
    const isEditOtherSelected = useMemo(() => {
        return editReasonPk === OTHER_REASON_VALUE;
    }, [editReasonPk]);

    // Build posology select options
    const posologySelectOptions = useMemo(() => [
        ...posologies.map(p => {
            // PRIORITY 1: Use static description if present
            if (p.description) {
                return { value: p.pk, label: p.description };
            }

            // PRIORITY 2: Fallback to dynamic generation
            const parts = p.parts || [];
            const doseString = parts.length > 0
                ? parts.map(part => {
                    const qty = part.quantity || 1;
                    const capsuleWord = qty > 1 ? 'CÁPSULAS' : 'CÁPSULA';
                    const qtyInWords = numberToPortugueseWords(qty).toUpperCase();
                    const partName = (part.name || part.description || '').toUpperCase();
                    return `${qty} (${qtyInWords}) ${capsuleWord} DE ${partName}`;
                  }).join(' E ')
                : '';

            let cycleString = '';
            if (p.duration) {
                const treatmentDays = p.duration - (p.pause_days || 0);
                if (p.pause_days && p.pause_days > 0) {
                    cycleString = `POR ${treatmentDays} DIAS E REALIZAR PAUSA POR ${p.pause_days} DIAS (CICLO DE ${p.duration} DIAS)`;
                } else {
                    cycleString = `POR ${p.duration} DIAS, SEM PAUSAS (CICLO DE ${p.duration} DIAS)`;
                }
            }

            const label = doseString && cycleString ? `ADMINISTRAR ${doseString} ${cycleString}` : doseString || cycleString || `Posologia ${p.pk}`;
            return { value: p.pk, label };
        }),
    ], [posologies]);

    // Build dosage description from selected posology
    const buildDosageDescription = useCallback((posologyValue) => {
        const posology = posologies.find(p => p.pk === posologyValue);
        if (posology) {
            // PRIORITY 1: Use static description if present
            if (posology.description) {
                return posology.description;
            }

            // PRIORITY 2: Fallback to dynamic generation
            const parts = posology.parts || [];
            const doseString = parts.length > 0
                ? parts.map(part => {
                    const qty = part.quantity || 1;
                    const capsuleWord = qty > 1 ? 'CÁPSULAS' : 'CÁPSULA';
                    const qtyInWords = numberToPortugueseWords(qty).toUpperCase();
                    const partName = (part.name || part.description || '').toUpperCase();
                    return `${qty} (${qtyInWords}) ${capsuleWord} DE ${partName}`;
                  }).join(' E ')
                : '';

            let cycleString = '';
            if (posology.duration) {
                const treatmentDays = posology.duration - (posology.pause_days || 0);
                if (posology.pause_days && posology.pause_days > 0) {
                    cycleString = `POR ${treatmentDays} DIAS E REALIZAR PAUSA POR ${posology.pause_days} DIAS (CICLO DE ${posology.duration} DIAS)`;
                } else {
                    cycleString = `POR ${posology.duration} DIAS, SEM PAUSAS (CICLO DE ${posology.duration} DIAS)`;
                }
            }

            return doseString && cycleString ? `ADMINISTRAR ${doseString} ${cycleString}` : doseString || cycleString || '';
        }
        return '';
    }, [posologies]);

    // Get translated dosage description - try to match with posology or return original
    const getTranslatedDosage = useCallback((data) => {
        if (!data?.dosage_description) return '-';

        // If data has posology_pk, use it to get translated label
        if (data.posology_pk) {
            const option = posologySelectOptions.find(opt => opt.value === data.posology_pk);
            if (option) return option.label;
        }

        // For custom posology with structured fields, build description including pause if applicable
        if (data.dosage_quantity && data.dosage_duration && data.medication && !data.posology_pk) {
            const qty = parseInt(data.dosage_quantity);
            const capsuleWord = qty === 1 ? 'CÁPSULA' : 'CÁPSULAS';
            const writtenNumber = numberToPortugueseWords(qty).toUpperCase();
            const medicationStr = `${data.medication?.strength || ''}${data.medication?.strength_unit || 'MG'}`.toUpperCase();

            const duration = parseInt(data.dosage_duration);
            const pauseDays = data.dosage_pause_days ? parseInt(data.dosage_pause_days) : 0;
            const totalCycleDays = duration + pauseDays;

            let cycleString = '';
            if (pauseDays > 0) {
                cycleString = `POR ${duration} DIAS E REALIZAR PAUSA POR ${pauseDays} DIAS (CICLO DE ${totalCycleDays} DIAS)`;
            } else {
                cycleString = `POR ${duration} DIAS, SEM PAUSAS (CICLO DE ${duration} DIAS)`;
            }

            return `ADMINISTRAR ${qty} (${writtenNumber}) ${capsuleWord} DE ${medicationStr} ${cycleString}`;
        }

        // Otherwise return stored description (already in Portuguese if created with posology)
        return data.dosage_description;
    }, [posologySelectOptions]);

    const handleViewDetails = useCallback((prescription) => {
        setSelectedPrescription(prescription);
        setDetailModalOpen(true);
        fetchPrescriptionHistory(prescription.pk);
    }, [fetchPrescriptionHistory]);

    const handlePrint = useCallback(async (prescription) => {
        try {
            await openPrescriptionPdfForPrint(prescription.pk);
        } catch (error) {
            console.error('Error printing prescription:', error);
            Notifications.toast(
                intl.get("PrintError").d("Error printing prescription"),
                Notifications.TYPE_ERROR
            );
        }
    }, []);

    const handleOpenCancelModal = useCallback((prescription) => {
        setSelectedPrescription(prescription);
        setCancelReasonPk('');
        setCancelCustomReason('');
        setCancelModalOpen(true);
    }, []);

    const handleCancelPrescription = useCallback(async () => {
        if (!selectedPrescription || !cancelReasonPk) return;

        // If "Other" is selected, require custom reason
        if (isCancelOtherSelected && (!cancelCustomReason || !cancelCustomReason.trim())) {
            Notifications.toast(
                intl.get("CustomReasonRequired").d("Please provide a custom reason."),
                Notifications.TYPE_ERROR
            );
            return;
        }

        setCanceling(true);
        try {
            const response = await fetchData('/prescription/cancel', 'POST', true, {
                prescription_pk: selectedPrescription.pk,
                prescription_reason_pk: isCancelOtherSelected ? null : Number(cancelReasonPk),
                custom_reason: isCancelOtherSelected ? cancelCustomReason.trim() : null
            });

            if (response.status === HTTP_STATUS.OK_200) {
                Notifications.toast(
                    intl.get("PrescriptionCanceled").d("Prescription canceled successfully"),
                    Notifications.TYPE_SUCCESS
                );
                setCancelModalOpen(false);
                table.current?.updateTable();
            } else {
                Notifications.toast(
                    response.detail || intl.get("CancelError").d("Error canceling prescription"),
                    Notifications.TYPE_ERROR
                );
            }
        } catch (error) {
            console.error('Error canceling prescription:', error);
            Notifications.toast(
                intl.get("CancelError").d("Error canceling prescription"),
                Notifications.TYPE_ERROR
            );
        } finally {
            setCanceling(false);
        }
    }, [selectedPrescription, cancelReasonPk, cancelCustomReason, isCancelOtherSelected]);

    // Edit handlers
    const handleOpenEditModal = useCallback((prescription) => {
        setSelectedPrescription(prescription);
        setEditDosage(prescription.dosage_description || '');
        setEditIndication(prescription.indication || '');
        setEditIndicationOther(prescription.indication_other || '');
        setEditReasonPk('');
        setEditCustomReason('');
        setEditPassword('');
        setShowPassword(false);
        setPasswordError('');
        setFieldErrors({});

        // Populate structured dosage fields
        setEditDosageQuantity(prescription.dosage_quantity || '');
        setEditDosageDuration(prescription.dosage_duration || '');
        setEditDosageNotes(prescription.dosage_notes || '');
        setEditDosagePauseEnabled(!!prescription.dosage_pause_days);
        setEditDosagePauseDays(prescription.dosage_pause_days || '');
        setEditSelectedMedication(prescription.medication_pk || prescription.medication?.pk || null);

        if (prescription.posology_pk) {
            setSelectedPosology(prescription.posology_pk);
        }
        setEditModalOpen(true);
    }, []);

    const handleCloseEditModal = useCallback(() => {
        setEditModalOpen(false);
        setEditDosage('');
        setEditIndication('');
        setEditIndicationOther('');
        setEditReasonPk('');
        setEditCustomReason('');
        setEditPassword('');
        setShowPassword(false);
        setPasswordError('');
        setSelectedPosology('');
        setSelectedPrescription(null);
        // Reset structured dosage fields
        setEditDosageQuantity('');
        setEditDosageDuration('');
        setEditDosageNotes('');
        setEditDosagePauseEnabled(false);
        setEditDosagePauseDays('');
        setEditSelectedMedication(null);
        setFieldErrors({});
    }, []);

    const handleEditPrescription = useCallback(async () => {
        if (!selectedPrescription) return;

        if (!editReasonPk) {
            Notifications.toast(
                intl.get("ReasonRequired").d("Reason Required"),
                Notifications.TYPE_ERROR
            );
            return;
        }

        // If "Other" is selected, require custom reason
        if (isEditOtherSelected && (!editCustomReason || !editCustomReason.trim())) {
            Notifications.toast(
                intl.get("CustomReasonRequired").d("Please provide a custom reason."),
                Notifications.TYPE_ERROR
            );
            return;
        }

        if (editIndication === IndicationEnum.OTHER && (!editIndicationOther || !editIndicationOther.trim())) {
            setFieldErrors(prev => ({ ...prev, editIndicationOther: intl.get("enter_other_indication").d("Please specify the other indication.") }));
            return;
        }

        if (!editPassword) {
            setPasswordError(intl.get("PasswordRequired").d("Password is required"));
            return;
        }

        setEditing(true);
        setPasswordError('');

        // Build dosage description and structured data based on selected posology
        let finalDosageDescription;
        let structuredData = {};

        finalDosageDescription = buildDosageDescription(selectedPosology);

        // Extract structured fields from selected posology (same logic as PrescriptionForm)
        // Always use posology values when a posology is selected — they define the treatment
        const selectedPosologyData = posologies.find(p => p.pk === selectedPosology);
        let finalDosageQuantity = null;
        let finalDosageDuration = null;
        let finalMedicationPk = null;
        let finalDosagePauseDays = null;

        if (selectedPosologyData) {
            const parts = selectedPosologyData.parts || [];
            finalDosageQuantity = parts.length > 0 ? (parts[0].quantity || 1) : null;
            finalDosageDuration = selectedPosologyData.duration || null;
            finalMedicationPk = selectedPosologyData.medication_pk || null;
            finalDosagePauseDays = selectedPosologyData.pause_days ?? null;
        }

        structuredData = {
            posology_pk: selectedPosology || null,
            dosage_notes: editDosageNotes || null,
            dosage_quantity: finalDosageQuantity,
            dosage_duration: finalDosageDuration,
            medication_pk: finalMedicationPk,
            dosage_pause_days: finalDosagePauseDays,
        };

        try {
            const response = await fetchData('/prescription/edit', 'POST', true, {
                prescription_pk: selectedPrescription.pk,
                dosage_description: finalDosageDescription,
                indication: editIndication ? Number(editIndication) : null,
                indication_other: editIndication === IndicationEnum.OTHER ? editIndicationOther : null,
                prescription_reason_pk: isEditOtherSelected ? null : Number(editReasonPk),
                custom_reason: isEditOtherSelected ? editCustomReason.trim() : null,
                password: editPassword,
                ...structuredData
            });

            if (response.status === HTTP_STATUS.OK_200) {
                Notifications.toast(
                    intl.get("PrescriptionUpdated").d("Prescription updated successfully"),
                    Notifications.TYPE_SUCCESS
                );
                handleCloseEditModal();
                table.current?.updateTable();
            } else if (response.status === HTTP_STATUS.FORBIDDEN_403) {
                setPasswordError(intl.get("InvalidPassword").d("Invalid password"));
            } else {
                Notifications.toast(
                    response.data?.detail || intl.get("EditError").d("Error updating prescription"),
                    Notifications.TYPE_ERROR
                );
            }
        } catch (error) {
            console.error('Error editing prescription:', error);
            if (error.response?.status === 403) {
                setPasswordError(intl.get("InvalidPassword").d("Invalid password"));
            } else {
                Notifications.toast(
                    intl.get("EditError").d("Error updating prescription"),
                    Notifications.TYPE_ERROR
                );
            }
        } finally {
            setEditing(false);
        }
    }, [selectedPrescription, editIndication, editIndicationOther, editReasonPk, editCustomReason, isEditOtherSelected, editPassword, selectedPosology, posologies, buildDosageDescription, handleCloseEditModal, editDosageNotes, editDosageQuantity, editDosageDuration, editSelectedMedication, editDosagePauseEnabled, editDosagePauseDays]);

    const canEdit = (prescription) => {
        if (prescription.status === PRESCRIPTION_STATUS.CANCELED) return false;
        if (prescription.status === PRESCRIPTION_STATUS.DISPENSED) return false;
        if (isExpired(prescription.expiration_date)) return false;
        return true;
    };

    const canCancel = (prescription) => {
        if (prescription.status === PRESCRIPTION_STATUS.CANCELED) return false;
        if (prescription.status === PRESCRIPTION_STATUS.DISPENSED) return false;
        if (isExpired(prescription.expiration_date)) return false;
        return true;
    };

    const renderHistoryTab = () => {
        if (loadingHistory) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (prescriptionHistory.length === 0) {
            return (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        {intl.get("NoHistoryFound").d("No change history found")}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ p: 2 }}>
                {prescriptionHistory.map((version) => (
                    <Box
                        key={version.version}
                        sx={{
                            mb: 2,
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HistoryIcon fontSize="small" color="action" />
                                <Typography variant="subtitle2">
                                    {intl.get("Version").d("Version")} {version.version}
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {dayjs(version.updated_on).format('DD/MM/YYYY HH:mm')}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                    {intl.get("Dosage").d("Dosage")}:
                                </Typography>
                                <Typography variant="body2">
                                    {getTranslatedDosage(version)}
                                </Typography>
                            </Grid>
                            {version.indication && (
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {intl.get("Indication").d("Indication")}:
                                    </Typography>
                                    <Typography variant="body2">
                                        {getIndicationText(version.indication)}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                    {intl.get("Status").d("Status")}:
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    {getStatusChip(version.status, version.expiration_date)}
                                </Box>
                            </Grid>
                            {version.prescription_reason_name && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        {intl.get("EditReason").d("Edit Reason")}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                        {PrescriptionReason.getText(version.prescription_reason_name)}
                                    </Typography>
                                </Grid>
                            )}
                            {version.cancellation_justification && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="error.main">
                                        {intl.get("CancellationReason").d("Cancellation Reason")}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                        {formatNotDispensedJustification(version.cancellation_justification)}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                ))}
            </Box>
        );
    };

    const header = [
        {
            column_name: "prescription_identifier",
            label: intl.get("PrescriptionNumber").d("Prescription #"),
            filter: true
        },
        {
            column_name: "patient",
            label: intl.get("Patient").d("Patient"),
            sortable: false,
            filter: true,
            function: (value) => value?.name || '-'
        },
        {
            column_name: "patient.cpf",
            label: intl.get("CPF").d("CPF"),
            sortable: false,
            filter: true,
            function: (value, row) => formatCpf(row.patient?.cpf) || '-'
        },
        {
            column_name: "prescription_date",
            label: intl.get("Date").d("Date"),
            function: (value) => value ? dayjs(value).format('DD/MM/YYYY') : '-'
        },
        {
            column_name: "expiration_date",
            label: intl.get("Expiration").d("Expiration"),
            function: (value) => value ? dayjs(value).format('DD/MM/YYYY') : '-'
        },
        {
            column_name: "status",
            label: intl.get("Status").d("Status"),
            function: (value, row) => getStatusChip(value, row.expiration_date)
        },
        {
            column_name: "actions",
            label: intl.get("Actions").d("Actions"),
            sortable: false,
            function: (value, row) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={intl.get("ViewDetails").d("View Details")}>
                        <span>
                            <DmsButton
                                icon={<VisibilityIcon />}
                                onClick={() => handleViewDetails(row)}
                                variant="secondary"
                                size="small"
                            />
                        </span>
                    </Tooltip>
                    <Tooltip title={intl.get("Print").d("Print")}>
                        <span>
                            <DmsButton
                                icon={<PrintIcon />}
                                onClick={() => handlePrint(row)}
                                variant="secondary"
                                size="small"
                            />
                        </span>
                    </Tooltip>
                    {canEdit(row) && (
                        <Tooltip title={intl.get("Edit").d("Edit")}>
                            <span>
                                <DmsButton
                                    icon={<EditIcon />}
                                    onClick={() => handleOpenEditModal(row)}
                                    variant="secondary"
                                    size="small"
                                />
                            </span>
                        </Tooltip>
                    )}
                    {canCancel(row) && (
                        <Tooltip title={intl.get("Cancel").d("Cancel")}>
                            <span>
                                <DmsButton
                                    icon={<CancelIcon />}
                                    onClick={() => handleOpenCancelModal(row)}
                                    variant="danger"
                                    size="small"
                                />
                            </span>
                        </Tooltip>
                    )}
                </Box>
            )
        }
    ];

    return (
        <DmsPage name={intl.get("PrescriptionHistory").d("Prescription History")}>
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant="h6">
                        {intl.get("PrescriptionHistory").d("Prescription History")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTable
                        header={header}
                        managed={true}
                        needs_authentication={true}
                        ref={table}
                        url={'/prescription/doctor/history'}
                        export_table={false}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            {/* Detail Modal */}
            <DmsModal
                isVisible={detailModalOpen}
                onClose={() => setDetailModalOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DmsModal.Header
                    title={intl.get("PrescriptionDetails").d("Prescription Details")}
                    onClose={() => setDetailModalOpen(false)}
                />
                <DmsModal.Body>
                    {selectedPrescription && (
                        <DmsTabber
                            pageName={intl.get("PrescriptionDetails").d("Prescription Details")}
                            tabs={[
                                {
                                    title: intl.get("Details").d("Details"),
                                    component: (
                                        <Box sx={{ p: 2 }}>
                                            {/* Header */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                                <Typography variant="h6">
                                                    {intl.get("Prescription").d("Prescription")} {selectedPrescription.prescription_identifier}
                                                </Typography>
                                                {getStatusChip(selectedPrescription.status, selectedPrescription.expiration_date)}
                                            </Box>

                                            {/* Patient Section */}
                                            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                    {intl.get("patient_data").d("Patient Data")}
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={3}>
                                                        <Typography variant="caption" color="text.secondary">{intl.get("Name").d("Name")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={9}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {selectedPrescription.patient?.name || '-'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <Typography variant="caption" color="text.secondary">CPF</Typography>
                                                    </Grid>
                                                    <Grid item xs={9}>
                                                        <Typography variant="body2">{formatCpf(selectedPrescription.patient?.cpf) || '-'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <Typography variant="caption" color="text.secondary">{intl.get("BirthDate").d("Birth Date")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={9}>
                                                        <Typography variant="body2">
                                                            {selectedPrescription.patient?.birth_date
                                                                ? dayjs(selectedPrescription.patient.birth_date).format('DD/MM/YYYY')
                                                                : '-'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <Typography variant="caption" color="text.secondary">{intl.get("gender").d("Gender")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={9}>
                                                        <Typography variant="body2">
                                                            {selectedPrescription.patient?.gender
                                                                ? (normalizeGender(selectedPrescription.patient.gender) === getGenderEnum().M.value
                                                                    ? intl.get("male").d("Male")
                                                                    : intl.get("female").d("Female"))
                                                                : '-'}
                                                        </Typography>
                                                    </Grid>
                                                    {selectedPrescription.patient?.rg && (
                                                        <>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("rg").d("RG")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={9}>
                                                                <Typography variant="body2">{selectedPrescription.patient.rg}</Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    <Grid item xs={3}>
                                                        <Typography variant="caption" color="text.secondary">{intl.get("PregnancyPotential").d("Pregnancy Potential")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={9}>
                                                        <Typography variant="body2">
                                                            {selectedPrescription.patient?.has_pregnancy_potential
                                                                ? intl.get("HasPregnancyPotential").d("Has Pregnancy Potential")
                                                                : intl.get("NoPregnancyPotentialShort").d("No Pregnancy Potential")}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                                {selectedPrescription.patient?.responsible_legal && selectedPrescription.patient.responsible_legal.length > 0 && (
                                                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'grey.300' }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.dark', display: 'block', mb: 1 }}>
                                                            {intl.get("ResponsibleLegal").d("Legal Guardian")}
                                                        </Typography>
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("Name").d("Name")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={9}>
                                                                <Typography variant="body2">{selectedPrescription.patient.responsible_legal[0].name}</Typography>
                                                            </Grid>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="text.secondary">CPF</Typography>
                                                            </Grid>
                                                            <Grid item xs={9}>
                                                                <Typography variant="body2">{formatCpf(selectedPrescription.patient.responsible_legal[0].cpf)}</Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Doctor Section */}
                                            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" color="success.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                    {intl.get("doctor_data").d("Doctor Data")}
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={3}>
                                                        <Typography variant="caption" color="text.secondary">{intl.get("prescribing_doctor").d("Prescribing Doctor")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={9}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {selectedPrescription.doctor_name || '-'}
                                                        </Typography>
                                                    </Grid>
                                                    {selectedPrescription.doctor_crm && (
                                                        <>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("CRM").d("CRM")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={9}>
                                                                <Typography variant="body2">
                                                                    {selectedPrescription.doctor_crm.crm}/{selectedPrescription.doctor_crm.crm_state}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {selectedPrescription.organization_branch && (
                                                        <>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("Institution").d("Institution")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={9}>
                                                                <Typography variant="body2">
                                                                    {selectedPrescription.organization_branch.name || '-'}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                            </Box>

                                            {/* Prescription Section */}
                                            <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600, listStyleType: 'none', '&::before': { content: 'none' } }}>
                                                    {intl.get("PrescriptionData").d("Prescription Data")}
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={3}>
                                                        <Typography variant="caption" color="text.secondary">{intl.get("PrescriptionDate").d("Prescription Date")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={9}>
                                                        <Typography variant="body2">
                                                            {dayjs(selectedPrescription.prescription_date).format('DD/MM/YYYY')}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <Typography variant="caption" color="text.secondary">{intl.get("ExpirationDate").d("Expiration Date")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={9}>
                                                        <Typography variant="body2">
                                                            {dayjs(selectedPrescription.expiration_date).format('DD/MM/YYYY')}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <Typography variant="caption" color="text.secondary">{intl.get("Dosage").d("Dosage")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={9}>
                                                        <Typography variant="body2">
                                                            {getTranslatedDosage(selectedPrescription)}
                                                        </Typography>
                                                    </Grid>
                                                    {selectedPrescription.dosage_notes && (
                                                        <>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("AdditionalNotes").d("Additional Notes")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={9}>
                                                                <Typography variant="body2" sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                                                                    {selectedPrescription.dosage_notes}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {selectedPrescription.indication && (
                                                        <>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("Indication").d("Indication")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={9}>
                                                                <Typography variant="body2">
                                                                    {getIndicationText(selectedPrescription.indication)}
                                                                </Typography>
                                                            </Grid>
                                                            {selectedPrescription.indication_other && (
                                                                <>
                                                                    <Grid item xs={3}>
                                                                        <Typography variant="caption" color="text.secondary">{intl.get("OtherIndication").d("Other Indication")}</Typography>
                                                                    </Grid>
                                                                    <Grid item xs={9}>
                                                                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                                            {selectedPrescription.indication_other}
                                                                        </Typography>
                                                                    </Grid>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                    {selectedPrescription.pregnancy_test_date && (
                                                        <>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("LastPregnancyTestDate").d("Last Pregnancy Test Date")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={9}>
                                                                <Typography variant="body2">
                                                                    {dayjs(selectedPrescription.pregnancy_test_date).format('DD/MM/YYYY')}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {selectedPrescription.medication && (
                                                        <>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("Medication").d("Medication")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={9}>
                                                                <Typography variant="body2">
                                                                    {selectedPrescription.medication.name}
                                                                    {selectedPrescription.medication.strength && ` - ${selectedPrescription.medication.strength}${selectedPrescription.medication.strength_unit || ''}`}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {selectedPrescription.dosage_quantity != null && (
                                                        <>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("Quantity").d("Quantity")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={9}>
                                                                <Typography variant="body2">{selectedPrescription.dosage_quantity}</Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {selectedPrescription.dosage_duration != null && (
                                                        <>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("Duration").d("Duration")} ({intl.get("Days").d("days")})</Typography>
                                                            </Grid>
                                                            <Grid item xs={9}>
                                                                <Typography variant="body2">{selectedPrescription.dosage_duration}</Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {selectedPrescription.dosage_pause_days != null && (
                                                        <>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("PauseDays").d("Pause Days")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={9}>
                                                                <Typography variant="body2">{selectedPrescription.dosage_pause_days}</Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                                {/* Contraceptive Methods */}
                                                {selectedPrescription.patient?.has_pregnancy_potential &&
                                                 normalizeGender(selectedPrescription.patient?.gender) === getGenderEnum().F.value &&
                                                 selectedPrescription.patient?.highly_effective_methods?.length > 0 && (
                                                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'primary.200' }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.dark', display: 'block', mb: 1.5 }}>
                                                            {intl.get("ContraceptiveMethodsUsed").d("Contraceptive Methods in Use")}
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                                                                    {intl.get("HighlyEffectiveMethods").d("Highly Effective Method")}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                    {selectedPrescription.patient.highly_effective_methods.map((method, index) => (
                                                                        <Chip key={index} label={method.reason} size="small" color="success" variant="outlined" sx={{ justifyContent: 'flex-start' }} />
                                                                    ))}
                                                                </Box>
                                                            </Grid>
                                                            {selectedPrescription.patient?.barrier_methods?.length > 0 && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                                                                        {intl.get("BarrierMethods").d("Barrier Method")}
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                        {selectedPrescription.patient.barrier_methods.map((method, index) => (
                                                                            <Chip key={index} label={method.reason} size="small" color="warning" variant="outlined" sx={{ justifyContent: 'flex-start' }} />
                                                                        ))}
                                                                    </Box>
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Confirmations Section */}
                                            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                    {intl.get("Confirmations").d("Confirmations")}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <CheckCircleIcon sx={{ color: selectedPrescription.liability_waiver_signed ? 'success.main' : 'grey.400', fontSize: 18 }} />
                                                        <Typography variant="body2">
                                                            {intl.get("CommitToSignDocuments").d("Liability Term: I commit to print and collect the signatures of the 3 generated documents.")}
                                                        </Typography>
                                                    </Box>
                                                    {selectedPrescription.allow_alternative_dispensation && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                                                            <Typography variant="body2">
                                                                {intl.get("AllowAlternativeDispensation").d("I authorize the pharmacist to dispense a different presentation if necessary")}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {selectedPrescription.patient?.has_pregnancy_potential &&
                                                     normalizeGender(selectedPrescription.patient?.gender) === getGenderEnum().F.value && (
                                                        <>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                                                                <Typography variant="body2">
                                                                    {intl.get("ConfirmNegativePregnancyTest").d("I confirm the test was performed with a negative result.")}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18, mt: 0.25 }} />
                                                                <Typography variant="body2">
                                                                    {intl.get("MedicalRecordsConfirmation").d("Medical records have been archived with each prescription, including pregnancy exclusion proof, one copy of the prescription, and contraceptive methods guidance.")}
                                                                </Typography>
                                                            </Box>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>

                                            {/* Dispensation Section - Only show for dispensed prescriptions */}
                                            {selectedPrescription.status === PRESCRIPTION_STATUS.DISPENSED && selectedPrescription.dispensing && selectedPrescription.dispensing.length > 0 && (
                                                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                    <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                        {intl.get("DispensationData").d("Dispensation Data")}
                                                    </Typography>
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={3}>
                                                            <Typography variant="caption" color="text.secondary">{intl.get("DispensationDate").d("Dispensation Date")}</Typography>
                                                        </Grid>
                                                        <Grid item xs={9}>
                                                            <Typography variant="body2">
                                                                {selectedPrescription.dispensing[0]?.created_on
                                                                    ? dayjs(selectedPrescription.dispensing[0].created_on).format('DD/MM/YYYY')
                                                                    : '-'}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <Typography variant="caption" color="text.secondary">{intl.get("box_count").d("Box Count")}</Typography>
                                                        </Grid>
                                                        <Grid item xs={9}>
                                                            <Typography variant="body2">{selectedPrescription.dispensing[0]?.box_count || 0}</Typography>
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <Typography variant="caption" color="text.secondary">{intl.get("capsule_count").d("Capsule Count")}</Typography>
                                                        </Grid>
                                                        <Grid item xs={9}>
                                                            <Typography variant="body2">{selectedPrescription.dispensing[0]?.capsule_count || 0}</Typography>
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <Typography variant="caption" color="text.secondary">{intl.get("TotalCapsules").d("Total Capsules")}</Typography>
                                                        </Grid>
                                                        <Grid item xs={9}>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {(selectedPrescription.dispensing[0]?.box_count || 0) * (selectedPrescription.dispensing[0]?.capsule_count || 0)}
                                                            </Typography>
                                                        </Grid>
                                                        {selectedPrescription.dispensing[0]?.dispensed_dose_strength && (
                                                            <>
                                                                <Grid item xs={3}>
                                                                    <Typography variant="caption" color="text.secondary">{intl.get("DoseStrength").d("Dose Strength")}</Typography>
                                                                </Grid>
                                                                <Grid item xs={9}>
                                                                    <Typography variant="body2">{selectedPrescription.dispensing[0].dispensed_dose_strength}mg</Typography>
                                                                </Grid>
                                                            </>
                                                        )}
                                                        {selectedPrescription.dispensing[0]?.batch?.number && (
                                                            <>
                                                                <Grid item xs={3}>
                                                                    <Typography variant="caption" color="text.secondary">{intl.get("Batch").d("Batch")}</Typography>
                                                                </Grid>
                                                                <Grid item xs={9}>
                                                                    <Typography variant="body2">{selectedPrescription.dispensing[0].batch.number}</Typography>
                                                                </Grid>
                                                            </>
                                                        )}
                                                        {selectedPrescription.dispensing[0]?.pharmacist && (
                                                            <>
                                                                <Grid item xs={3}>
                                                                    <Typography variant="caption" color="text.secondary">{intl.get("Pharmacist").d("Pharmacist")}</Typography>
                                                                </Grid>
                                                                <Grid item xs={9}>
                                                                    <Typography variant="body2">
                                                                        {selectedPrescription.dispensing[0].pharmacist.person?.name ||
                                                                         `CRF ${selectedPrescription.dispensing[0].pharmacist.crf}` || '-'}
                                                                    </Typography>
                                                                </Grid>
                                                            </>
                                                        )}
                                                    </Grid>
                                                </Box>
                                            )}

                                            {/* Not Dispensed Justification Section */}
                                            {selectedPrescription.status === PRESCRIPTION_STATUS.NOT_DISPENSED && selectedPrescription.dispensing && selectedPrescription.dispensing.length > 0 && (
                                                <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                                                    <Typography variant="subtitle2" color="warning.dark" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                        {intl.get("NotDispensedJustification").d("Not Dispensed - Justification")}
                                                    </Typography>
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={3}>
                                                            <Typography variant="caption" color="text.secondary">{intl.get("Date").d("Date")}</Typography>
                                                        </Grid>
                                                        <Grid item xs={9}>
                                                            <Typography variant="body2">
                                                                {selectedPrescription.dispensing[0]?.created_on
                                                                    ? dayjs(selectedPrescription.dispensing[0].created_on).format('DD/MM/YYYY')
                                                                    : '-'}
                                                            </Typography>
                                                        </Grid>
                                                        {selectedPrescription.dispensing[0]?.pharmacist && (
                                                            <>
                                                                <Grid item xs={3}>
                                                                    <Typography variant="caption" color="text.secondary">{intl.get("Pharmacist").d("Pharmacist")}</Typography>
                                                                </Grid>
                                                                <Grid item xs={9}>
                                                                    <Typography variant="body2">
                                                                        {selectedPrescription.dispensing[0].pharmacist.person?.name ||
                                                                         `CRF ${selectedPrescription.dispensing[0].pharmacist.crf}` || '-'}
                                                                    </Typography>
                                                                </Grid>
                                                            </>
                                                        )}
                                                        <Grid item xs={3}>
                                                            <Typography variant="caption" color="text.secondary">{intl.get("Justification").d("Justification")}</Typography>
                                                        </Grid>
                                                        <Grid item xs={9}>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {selectedPrescription.dispensing[0]?.dispensation_justification || '-'}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            )}

                                            {/* Cancellation Section */}
                                            {(selectedPrescription.prescription_reason || selectedPrescription.cancellation_justification) && (
                                                <Box sx={{ mb: 2, p: 2, bgcolor: 'error.50', borderRadius: 1 }}>
                                                    <Typography variant="subtitle2" color="error.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                        {intl.get("CancellationReason").d("Cancellation Reason")}
                                                    </Typography>
                                                    <Grid container spacing={1}>
                                                        {selectedPrescription.prescription_reason && (
                                                            <>
                                                                <Grid item xs={3}>
                                                                    <Typography variant="caption" color="text.secondary">{intl.get("Reason").d("Reason")}</Typography>
                                                                </Grid>
                                                                <Grid item xs={9}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {PrescriptionReason.getText(selectedPrescription.prescription_reason.name)}
                                                                    </Typography>
                                                                </Grid>
                                                            </>
                                                        )}
                                                        {selectedPrescription.cancellation_justification && (
                                                            <>
                                                                <Grid item xs={3}>
                                                                    <Typography variant="caption" color="text.secondary">{intl.get("Details").d("Details")}</Typography>
                                                                </Grid>
                                                                <Grid item xs={9}>
                                                                    <Typography variant="body2">
                                                                        {formatNotDispensedJustification(selectedPrescription.cancellation_justification)}
                                                                    </Typography>
                                                                </Grid>
                                                            </>
                                                        )}
                                                    </Grid>
                                                </Box>
                                            )}
                                        </Box>
                                    )
                                },
                                {
                                    title: intl.get("ChangeHistory").d("Change History"),
                                    component: renderHistoryTab()
                                }
                            ]}
                        />
                    )}
                </DmsModal.Body>
                <DmsModal.Footer>
                    <DmsButton
                        text={intl.get("Close").d("Close")}
                        onClick={() => setDetailModalOpen(false)}
                    />
                    <DmsButton
                        icon={<PrintIcon />}
                        text={intl.get("Print").d("Print")}
                        onClick={() => selectedPrescription && handlePrint(selectedPrescription)}
                    />
                </DmsModal.Footer>
            </DmsModal>

            {/* Cancel Modal */}
            <DmsModal
                isVisible={cancelModalOpen}
                onClose={() => setCancelModalOpen(false)}
            >
                <DmsModal.Header
                    title={intl.get("CancelPrescription").d("Cancel Prescription")}
                    onClose={() => setCancelModalOpen(false)}
                />
                <DmsModal.Body>
                    <Box sx={{ p: 2 }}>
                        <DmsAlertWarning
                            message={intl.get("CancelPrescriptionWarning").d("Are you sure you want to cancel this prescription? This action cannot be undone.")}
                        />
                        <Box sx={{ mt: 3 }}>
                            <DmsSelect
                                label={intl.get("CancellationJustification").d("Cancellation Justification")}
                                value={cancelReasonPk}
                                onChange={(value) => {
                                    setCancelReasonPk(value);
                                    if (value !== OTHER_REASON_VALUE) {
                                        setCancelCustomReason('');
                                    }
                                }}
                                options={prescriptionReasonOptions}
                                fullWidth
                                required
                                sx={{ mb: isCancelOtherSelected ? 2 : 0 }}
                            />
                            {isCancelOtherSelected && (
                                <DmsTextField
                                    label={intl.get("CustomReason").d("Custom Reason")}
                                    value={cancelCustomReason}
                                    onChange={(e) => setCancelCustomReason(e.target.value)}
                                    multiline
                                    rows={3}
                                    fullWidth
                                    required
                                    placeholder={intl.get("EnterCustomReasonPlaceholder").d("Enter the custom reason...")}
                                    sx={{ mt: 2 }}
                                />
                            )}
                        </Box>
                    </Box>
                </DmsModal.Body>
                <DmsModal.Footer>
                    <DmsButton
                        text={intl.get("Back").d("Back")}
                        onClick={() => setCancelModalOpen(false)}
                        variant="secondary"
                        disabled={canceling}
                    />
                    <DmsButton
                        text={canceling ? intl.get("Canceling").d("Canceling...") : intl.get("ConfirmCancel").d("Confirm Cancellation")}
                        onClick={handleCancelPrescription}
                        variant="danger"
                        disabled={!cancelReasonPk || (isCancelOtherSelected && !cancelCustomReason?.trim()) || canceling}
                    />
                </DmsModal.Footer>
            </DmsModal>

            {/* Edit Modal */}
            <DmsModal
                isVisible={editModalOpen}
                onClose={handleCloseEditModal}
                maxWidth="md"
            >
                <DmsModal.Header
                    title={intl.get("EditPrescription").d("Edit Prescription")}
                    onClose={handleCloseEditModal}
                />
                <DmsModal.Body>
                    <Box sx={{ p: 2 }}>
                        {selectedPrescription && (
                            <>
                                <DmsAlertInfo
                                    message={intl.get("EditPrescriptionInfo").d("Changes to this prescription will be logged and require password confirmation.")}
                                />

                                <Box sx={{ mt: 3, mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        {intl.get("PrescriptionNumber").d("Prescription #")}: {selectedPrescription.prescription_identifier}
                                    </Typography>
                                    <Typography variant="body2">
                                        {intl.get("Patient").d("Patient")}: {selectedPrescription.patient?.name || '-'}
                                    </Typography>
                                </Box>

                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <DmsSelect
                                            label={`${intl.get("Posology").d("Posology")} *`}
                                            value={selectedPosology}
                                            onChange={(value) => {
                                                setSelectedPosology(value);
                                                setEditDosage(buildDosageDescription(value));
                                            }}
                                            options={posologySelectOptions}
                                            fullWidth
                                        />
                                    </Grid>

                                    {selectedPosology && (
                                        <Grid item xs={12}>
                                            <DmsTextField
                                                label={intl.get("AdditionalNotes").d("Additional Notes")}
                                                name="dosageNotes"
                                                fullWidth
                                                multiline
                                                rows={2}
                                                value={editDosageNotes}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    if (newValue.length <= 180) {
                                                        setEditDosageNotes(newValue);
                                                    }
                                                }}
                                                placeholder={intl.get("AdditionalNotesPlaceholder").d("Optional notes about the posology")}
                                                helperText={`${editDosageNotes.length}/180`}
                                                inputProps={{ maxLength: 180 }}
                                            />
                                        </Grid>
                                    )}

                                    <Grid item xs={12} sm={editIndication === IndicationEnum.OTHER ? 6 : 12}>
                                        <DmsSelect
                                            label={intl.get("Indication").d("Indication")}
                                            value={editIndication}
                                            onChange={(value) => {
                                                setEditIndication(value);
                                                if (fieldErrors.editIndicationOther) setFieldErrors(prev => ({ ...prev, editIndicationOther: null }));
                                            }}
                                            options={getIndicationOptions()}
                                            fullWidth
                                        />
                                    </Grid>

                                    {editIndication === IndicationEnum.OTHER && (
                                        <Grid item xs={12} sm={6}>
                                            <DmsTextField
                                                label={`${intl.get("OtherIndication").d("Other")} *`}
                                                value={editIndicationOther}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    if (newValue.length <= 115) {
                                                        setEditIndicationOther(newValue);
                                                        if (fieldErrors.editIndicationOther) setFieldErrors(prev => ({ ...prev, editIndicationOther: null }));
                                                    }
                                                }}
                                                fullWidth
                                                error={!!fieldErrors.editIndicationOther}
                                                helperText={fieldErrors.editIndicationOther || `${editIndicationOther.length}/115`}
                                                inputProps={{ maxLength: 115 }}
                                            />
                                        </Grid>
                                    )}

                                    <Grid item xs={12}>
                                        <DmsSelect
                                            label={`${intl.get("EditJustification").d("Justification for changes")} *`}
                                            value={editReasonPk}
                                            onChange={(value) => {
                                                setEditReasonPk(value);
                                                if (value !== OTHER_REASON_VALUE) {
                                                    setEditCustomReason('');
                                                }
                                            }}
                                            options={editPrescriptionReasonOptions}
                                            fullWidth
                                            required
                                            sx={{ mb: isEditOtherSelected ? 2 : 0 }}
                                        />
                                        {isEditOtherSelected && (
                                            <DmsTextField
                                                label={intl.get("CustomReason").d("Custom Reason")}
                                                value={editCustomReason}
                                                onChange={(e) => setEditCustomReason(e.target.value)}
                                                fullWidth
                                                multiline
                                                rows={3}
                                                required
                                                placeholder={intl.get("EnterCustomReasonPlaceholder").d("Enter the custom reason...")}
                                                sx={{ mt: 2 }}
                                            />
                                        )}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={{
                                            p: 2,
                                            bgcolor: 'warning.50',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'warning.200'
                                        }}>
                                            <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LockIcon fontSize="small" />
                                                {intl.get("PasswordConfirmation").d("Password Confirmation")}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {intl.get("PasswordConfirmationInfo").d("Enter your password to confirm and authorize these changes.")}
                                            </Typography>
                                            <DmsTextField
                                                name="edit_password"
                                                label={intl.get("Password").d("Password")}
                                                type={showPassword ? 'text' : 'password'}
                                                value={editPassword}
                                                onChange={(e) => {
                                                    setEditPassword(e.target.value);
                                                    setPasswordError('');
                                                }}
                                                fullWidth
                                                required
                                                error={!!passwordError}
                                                helperText={passwordError}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <Tooltip title={showPassword ? intl.get("Tooltip.Action.HidePassword").d("Ocultar senha") : intl.get("Tooltip.Action.ShowPassword").d("Mostrar senha")}>
                                                                <IconButton
                                                                    onClick={() => setShowPassword(!showPassword)}
                                                                    edge="end"
                                                                >
                                                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                                </IconButton>
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </Box>
                </DmsModal.Body>
                <DmsModal.Footer>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '100%' }}>
                        <DmsButton
                            text={intl.get("Cancel").d("Cancel")}
                            onClick={handleCloseEditModal}
                            variant="secondary"
                            disabled={editing}
                        />
                        <DmsButton
                            text={editing
                                ? intl.get("Saving").d("Saving...")
                                : intl.get("SaveChanges").d("Save Changes")
                            }
                            onClick={handleEditPrescription}
                            variant="primary"
                            disabled={!editReasonPk || (isEditOtherSelected && !editCustomReason?.trim()) || !editPassword || editing}
                            startIcon={editing ? <CircularProgress size={18} color="inherit" /> : null}
                        />
                    </Box>
                </DmsModal.Footer>
            </DmsModal>
        </DmsPage>
    );
};

export default PrescriptionHistoryPage;
